import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import {
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase,
    akvToResource,
    akvToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import {
    connectionStringPartToResource,
    connectionStringPartToModel
} from 'app/shared/utils/linkedservice-parameter.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import {
    getEncryptionAuthTypeToModel,
    getEncryptionAuthTypeToResource,
    sqlAdditionalPropertiesKeyOptions
} from 'app/registries/consts/metadata-consts';
import {
    AdapterContext,
    ReferenceAdapter,
    Credential,
    sqlEncryptedAuthMethod,
    ParameterConfig,
    sqlAdditionalPropertiesAdapts,
    sqlAdditionalPropertiesKeyList,
    SqlAdditionalPropertiesKeyType,
    SqlServerLinkedService,
    SqlServerAuthType
} from 'shell-models-lib';
import { toParameterClass, toParameterObject } from './parameter-config.util';
import { compact } from 'lodash';
import { sqlConnectionStringKeyAlias } from './azure-sql-linkedservice.adapter';

const sqlServerV1Adapts = {
    connectionString: (_context: AdapterContext, _value: unknown, ls: SqlServerLinkedService) => {
        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
        // won't cover fully by json for now
        if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
            if (ls.password instanceof AzureKeyVault) {
                return '*';
            }
        }
        return 'secret';
    },
    encryptedCredential: '*'
};

const sqlServerV2Adapts = {
    server: '*',
    database: '*',
    encrypt: '*',
    trustServerCertificate: '*',
    hostNameInCertificate: '*',
    authenticationType: '*',
    ...sqlAdditionalPropertiesAdapts,
    credential: '*'
};

const sharedAdapts = {
    userName: '*',
    password: 'secret',
    alwaysEncryptedSettings: {
        alwaysEncryptedAkvAuthType: '*',
        servicePrincipalId: '*',
        servicePrincipalKey: 'secret',
        credential: '*'
    }
};

export const sqlServerLinkedServiceAdapts = {
    name: '*',
    properties: {
        description: '*',
        type: '*',
        connectVia: '*',
        typeProperties: {
            ...sharedAdapts,
            ...sqlServerV1Adapts,
            ...sqlServerV2Adapts
        },
        parameters: '*'
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlServerLinkedService),
    asynchronous: true,
    adapts: sqlServerLinkedServiceAdapts
})
export class SqlServerLinkedServiceAdapter extends LinkedServiceAdapter<SqlServerLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => SqlServerLinkedService {
        return SqlServerLinkedService;
    }

    protected model(): IModel {
        return SqlServerLinkedService.MODEL;
    }

    protected override _parseConnectionString(
        connectionString: string,
        linkedService: SqlServerLinkedService,
        additionalFieldsParameterizable: boolean = false
    ): void {
        try {
            this._updateModelFromConnectionString(
                connectionString,
                linkedService,
                false,
                additionalFieldsParameterizable
            );
        } catch (e) {
            this._logError(
                `AzureSqlLinkedServiceAdapter parse connection string error: "connection string": ${connectionString}, "error":${e}`
            );
        }
    }

    protected async propertiesToResourceAsync(
        linkedService: SqlServerLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SqlServerLinkedService;
        properties.type = Metamodel.modelOf(linkedService).name;

        if (linkedService.sqlVersion === 'v2') {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, SqlServerLinkedService.SERVER_NAME),
                database: this.valueToResource(linkedService, SqlServerLinkedService.DATABASE_NAME),
                encrypt: this.valueToResource(linkedService, SqlServerLinkedService.ENCRYPT),
                trustServerCertificate: this.valueToResource(
                    linkedService,
                    SqlServerLinkedService.TRUST_SERVER_CERTIFICATE
                ),
                hostNameInCertificate:
                    (SqlServerLinkedService.HOST_NAME_IN_CERTIFICATE.templateValue(linkedService) ||
                        linkedService.hostNameInCertificate) &&
                    (!stringIsNullOrEmpty(
                        SqlServerLinkedService.TRUST_SERVER_CERTIFICATE.templateValue(linkedService)
                    ) ||
                        linkedService.trustServerCertificate !== true)
                        ? this.valueToResource(linkedService, SqlServerLinkedService.HOST_NAME_IN_CERTIFICATE)
                        : undefined
            };

            properties.typeProperties.authenticationType = '' + linkedService.authenticationType;
            if (linkedService.authenticationType === SqlServerAuthType.UserAssignedManagedIdentity) {
                if (linkedService.credential != null) {
                    properties.typeProperties.credential = ReferenceAdapter.toResource(
                        linkedService.credential,
                        this.context
                    ) as Model.CredentialReference;
                }
            } else if (
                linkedService.authenticationType === SqlServerAuthType.SQLAuthentication ||
                linkedService.authenticationType === SqlServerAuthType.WindowsAuthentication
            ) {
                properties.typeProperties.userName = this.valueToResource(
                    linkedService,
                    SqlServerLinkedService.USER_NAME
                );
                properties.typeProperties.password = parameterizablePasswordOrAKVtoSecretBase(
                    linkedService,
                    SqlServerLinkedService.PASSWORD,
                    this.context
                );
            }

            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }
        } else {
            let connectionString: string | Model.SecretBase;

            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService();

                if (linkedService.authenticationType === SqlServerAuthType.WindowsAuthentication) {
                    builder.add('Integrated Security', 'True');
                } else if (linkedService.authenticationType === SqlServerAuthType.SQLAuthentication) {
                    builder.add('Integrated Security', 'False');
                } else {
                    builder.add('Integrated Security', '');
                }
                builder.add(
                    'Data Source',
                    connectionStringPartToResource(linkedService, SqlServerLinkedService.SERVER_NAME)
                );
                builder.add(
                    'Initial Catalog',
                    connectionStringPartToResource(linkedService, SqlServerLinkedService.DATABASE_NAME)
                );
                if (linkedService.authenticationType === SqlServerAuthType.SQLAuthentication) {
                    builder.add(
                        'User ID',
                        connectionStringPartToResource(linkedService, SqlServerLinkedService.USER_NAME)
                    );
                    if (!(linkedService.password instanceof AzureKeyVault)) {
                        const password = connectionStringPartToResource(linkedService, SqlServerLinkedService.PASSWORD);
                        if (password) {
                            builder.add('Password', password);
                        }
                    }
                }
                linkedService.additionalFields?.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
                connectionString =
                    linkedService.serverName === Constants.maskedCredential &&
                    linkedService.databaseName === Constants.maskedCredential &&
                    linkedService.userName === Constants.maskedCredential &&
                    linkedService.password === Constants.maskedCredential
                        ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ({
                              type: 'SecureString',
                              value: Constants.maskedCredential
                          } as Model.SecureString)
                        : this._connectionStringToResource(builder.connectionString);
            } else {
                connectionString = akvToResource(linkedService.referedAKV, this.context);
            }

            properties.typeProperties = {
                connectionString,
                userName:
                    linkedService.authenticationType === SqlServerAuthType.WindowsAuthentication
                        ? this.valueToResource(linkedService, SqlServerLinkedService.USER_NAME)
                        : undefined,
                password:
                    linkedService.authenticationType === SqlServerAuthType.WindowsAuthentication
                        ? parameterizablePasswordOrAKVtoSecretBase(
                              linkedService,
                              SqlServerLinkedService.PASSWORD,
                              this.context
                          )
                        : !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault
                        ? akvToResource(linkedService.password, this.context)
                        : undefined
            };
        }

        if (!linkedService.alwaysEncrypted) {
            return linkedServiceResource;
        }

        const alwaysEncryptedSettings: Model.SqlEncryptionProperties = {
            alwaysEncryptedAkvAuthType: getEncryptionAuthTypeToResource(
                linkedService.keyStoreAuthType as AuthenticationType
            )
        };
        if (linkedService.keyStoreAuthType === AuthenticationType.ServicePrincipal) {
            alwaysEncryptedSettings.servicePrincipalId = this.valueToResource(
                linkedService,
                SqlServerLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_ID
            );
            alwaysEncryptedSettings.servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                SqlServerLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_KEY,
                this.context
            );
        } else if (linkedService.keyStoreAuthType === AuthenticationType.UserAssignedManagedIdentity) {
            if (linkedService.alwaysEncryptionCredential != null) {
                alwaysEncryptedSettings.credential = ReferenceAdapter.toResource(
                    linkedService.alwaysEncryptionCredential,
                    this.context
                ) as Model.CredentialReference;
            }
        }
        properties.typeProperties.alwaysEncryptedSettings = alwaysEncryptedSettings;
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: SqlServerLinkedService
    ): Promise<SqlServerLinkedService> {
        const properties = linkedServiceResource.properties as Model.SqlServerLinkedService;

        if (!__IS_FABRIC_DPE__ && properties.typeProperties.connectionString == null) {
            linkedService.sqlVersion = 'v2';
            ValueAdapter.toModel(linkedService, SqlServerLinkedService.SERVER_NAME, properties.typeProperties.server);
            ValueAdapter.toModel(
                linkedService,
                SqlServerLinkedService.DATABASE_NAME,
                properties.typeProperties.database
            );
            ValueAdapter.toModel(linkedService, SqlServerLinkedService.ENCRYPT, properties.typeProperties.encrypt);
            ValueAdapter.toModel(
                linkedService,
                SqlServerLinkedService.TRUST_SERVER_CERTIFICATE,
                properties.typeProperties.trustServerCertificate
            );
            ValueAdapter.toModel(
                linkedService,
                SqlServerLinkedService.HOST_NAME_IN_CERTIFICATE,
                properties.typeProperties.hostNameInCertificate
            );

            linkedService.authenticationType = properties.typeProperties
                .authenticationType as unknown as SqlServerAuthType;
            if (properties.typeProperties.authenticationType === SqlServerAuthType.UserAssignedManagedIdentity) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
            } else if (
                properties.typeProperties.authenticationType === SqlServerAuthType.SQLAuthentication ||
                properties.typeProperties.authenticationType === SqlServerAuthType.WindowsAuthentication
            ) {
                ValueAdapter.toModel(linkedService, SqlServerLinkedService.USER_NAME, properties.typeProperties.userName);
                parameterizableSecretBaseToModel(
                    linkedService,
                    SqlServerLinkedService.PASSWORD,
                    properties.typeProperties.password,
                    this.context
                );
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const additionalPropertiesObj: { [key: string]: any } = {};
            for (const propertyName in properties.typeProperties) {
                if (sqlAdditionalPropertiesKeyList.includes(propertyName)) {
                    additionalPropertiesObj[propertyName] =
                        properties.typeProperties[propertyName as keyof Model.SqlServerLinkedServiceTypeProperties];
                }
            }
            linkedService.parameterizableAdditionalFields = toParameterClass(
                additionalPropertiesObj,
                linkedService
            ) as Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
        } else {
            linkedService.sqlVersion = 'v1';
            // Cleanup, in case non of the if-else statement got executed
            SqlServerLinkedService.SERVER_NAME.setValue(linkedService, undefined);
            SqlServerLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
            SqlServerLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
            SqlServerLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
            SqlServerLinkedService.USER_NAME.setValue(linkedService, undefined);
            SqlServerLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
            SqlServerLinkedService.PASSWORD.setValue(linkedService, undefined);
            SqlServerLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            linkedService.referedAKV = undefined;
            if (isString(properties.typeProperties.connectionString)) {
                this._updateModelFromConnectionString(properties.typeProperties.connectionString, linkedService);
                if (!properties.typeProperties.userName && properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        SqlServerLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.serverName = Constants.maskedCredential;
                linkedService.databaseName = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                if (!properties.typeProperties.userName && properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        SqlServerLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (
                properties.typeProperties.connectionString &&
                (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret'
            ) {
                linkedService.referedAKV = akvToModel(
                    properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference,
                    this.context
                );
                linkedService.enableAKV = true;
            }

            linkedService.authenticationType = properties.typeProperties.userName
                ? SqlServerAuthType.WindowsAuthentication
                : SqlServerAuthType.SQLAuthentication;
            // for win auth
            if (properties.typeProperties.userName) {
                ValueAdapter.toModel(
                    linkedService,
                    SqlServerLinkedService.USER_NAME,
                    properties.typeProperties.userName
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    SqlServerLinkedService.PASSWORD,
                    properties.typeProperties.password,
                    this.context
                );
            }
        }

        if (properties.typeProperties.alwaysEncryptedSettings) {
            linkedService.alwaysEncrypted = true;
            const authType = properties.typeProperties.alwaysEncryptedSettings.alwaysEncryptedAkvAuthType;
            linkedService.keyStoreAuthType = getEncryptionAuthTypeToModel(authType);
            if (authType === sqlEncryptedAuthMethod.servicePrincipal) {
                ValueAdapter.toModel(
                    linkedService,
                    SqlServerLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.alwaysEncryptedSettings.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    SqlServerLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.alwaysEncryptedSettings.servicePrincipalKey,
                    this.context
                );
            } else if (authType === sqlEncryptedAuthMethod.userAssigedManagedIdentity) {
                linkedService.alwaysEncryptionCredential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.alwaysEncryptedSettings.credential,
                    this.context
                );
            } else if (authType !== sqlEncryptedAuthMethod.managedIdentity) {
                // unknown type
                this._logError(
                    `SqlServerLinkedServiceAdapter encountered unknown alwaysEncryptedAkvAuthType: ${authType}`
                );
            }
        }

        return linkedService;
    }

    private _updateModelFromConnectionString(
        connectionString: string,
        linkedService: SqlServerLinkedService,
        fromLSResource: boolean = true,
        additionalFieldsParameterizable: boolean = false
    ): void {
        const builder = new DbConnectionStringBuilderService();
        builder.parse(connectionString);

        connectionStringPartToModel(
            linkedService,
            SqlServerLinkedService.SERVER_NAME,
            builder.getValueUsingAlias('Data Source', sqlConnectionStringKeyAlias, false) || ''
        );
        connectionStringPartToModel(
            linkedService,
            SqlServerLinkedService.DATABASE_NAME,
            builder.getValueUsingAlias('Initial Catalog', sqlConnectionStringKeyAlias, false) || (__IS_FABRIC_DPE__ ? undefined : '')
        );
        connectionStringPartToModel(
            linkedService,
            SqlServerLinkedService.USER_NAME,
            builder.getValueUsingAlias('User ID', sqlConnectionStringKeyAlias, false)
        );

        if (fromLSResource || builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false)) {
            connectionStringPartToModel(
                linkedService,
                SqlServerLinkedService.PASSWORD,
                builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false) || '',
                fromLSResource
            );
        }

        if (!fromLSResource) {
                const integratedSecurityValue = builder.getValueUsingAlias('Integrated Security', sqlConnectionStringKeyAlias, false);
                const hasUserId =
                    !stringIsNullOrEmpty(SqlServerLinkedService.USER_NAME.templateValue(linkedService)) ||
                    !stringIsNullOrEmpty(SqlServerLinkedService.USER_NAME.value(linkedService));
                linkedService.authenticationType = hasUserId
                    ? SqlServerAuthType.SQLAuthentication
                    : integratedSecurityValue
                    ? SqlServerAuthType.WindowsAuthentication
                    : linkedService.authenticationType || SqlServerAuthType.WindowsAuthentication;
        }

        const remainedKeys = [
            ...sqlConnectionStringKeyAlias['Data Source'],
            ...sqlConnectionStringKeyAlias['Initial Catalog'],
            ...sqlConnectionStringKeyAlias['User ID'],
            ...sqlConnectionStringKeyAlias['Password']
        ];

        if (additionalFieldsParameterizable) {
            if (builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    SqlServerLinkedService.ENCRYPT,
                    builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)
                );
            }

            const tscVaule = builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false);
            if (tscVaule) {
                const tscBooleanValue =
                    tscVaule.toLowerCase() === 'true' ? true : tscVaule.toLowerCase() === 'false' ? false : tscVaule;
                if (tscBooleanValue === true || tscBooleanValue === false) {
                    SqlServerLinkedService.TRUST_SERVER_CERTIFICATE.setValue(linkedService, tscBooleanValue);
                } else {
                    connectionStringPartToModel(
                        linkedService,
                        SqlServerLinkedService.TRUST_SERVER_CERTIFICATE,
                        builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false)
                    );
                }
            }

            if (builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    SqlServerLinkedService.HOST_NAME_IN_CERTIFICATE,
                    builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)
                );
            }

            remainedKeys.push(...[
                'Encrypt',
                ...sqlConnectionStringKeyAlias['Trust Server Certificate'] || 'Trust Server Certificate'
            ]);

            linkedService.parameterizableAdditionalFields = compact(
                builder.getRemainedKeyValuePairs(remainedKeys).map((kv) => {
                    const parameterObject = new ParameterConfig<SqlAdditionalPropertiesKeyType>();
                    const additionalParameter = sqlAdditionalPropertiesKeyOptions.find(
                        (option) => option.value.toLowerCase() === kv.key.toLowerCase().replace(/\s*/g, '')
                    );
                    if (additionalParameter) {
                        parameterObject.name = additionalParameter.value as SqlAdditionalPropertiesKeyType;
                        parameterObject.value = kv.value;
                        parameterObject.type = additionalParameter.type;
                        return parameterObject;
                    }
                    return undefined;
                })
            );
        } else {
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(remainedKeys);
        }
    }
}
